import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { MainLayout } from "./components/layout/MainLayout";
import { InboxLayout } from "./components/layout/InboxLayout";
import { RequireRole } from "./components/auth/RequireRole";
import { Toaster } from "./components/ui/sonner";

// Landing Page
import LandingPage from "./pages/landing/LandingPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import PendingApprovalPage from "./pages/auth/PendingApprovalPage";
import OnboardingPage from "./pages/onboarding/OnboardingPage";

// Main Pages
import MorningOverview from "./pages/dashboard/MorningOverview";
import CompaniesPage from "./pages/companies/CompaniesPage";
import DocumentsPage from "./pages/documents/DocumentsPage";
import ReviewScreenPage from './pages/review/ReviewScreenPage';
import DocumentReviewPage from "./pages/documents/DocumentReviewPage";
// VouchersPage retired from the accountant UI (route now redirects to Documents).
// Import removed; page file kept on disk for easy revert.
// Legacy Reconciliation page hidden (route redirects to Bank matching). Import kept for easy revert.
// import ReconciliationPage from "./pages/reconciliation/ReconciliationPage";
import BankStatementPage from "./pages/bank/BankStatementPage";
import BankMatchingPage from "./pages/bank/BankMatchingPage";
import BankCodingPage from "./pages/bank/BankCodingPage";
import BankExportPage from "./pages/bank/BankExportPage";
import VATPage from "./pages/vat/VATPage";
import ActivityPage from "./pages/activity/ActivityPage";
import BillingPage from "./pages/billing/BillingPage";
import AccountantDashboard from "./pages/accountant/AccountantDashboard";
import AdminPanel from "./pages/admin/AdminPanel";
import AdminFirmsPage from "./pages/admin/AdminFirmsPage";
import AdminDashboardPage from "./pages/admin/AdminDashboardPage";
import AdminActivityPage from "./pages/admin/AdminActivityPage";
import SettingsPage from "./pages/settings/SettingsPage";
import AIDashboardPage from "./pages/ai-dashboard/AIDashboardPage";

// End-user inbox (Stage 1)
import InboxPage from "./pages/inbox/InboxPage";

// Staff management (Stage 4 — firm owner)
import StaffPage from "./pages/staff/StaffPage";

// Other documents / Files (non-invoice)
import FilesPage from "./pages/files/FilesPage";

// Portfolio Risk Platform Pages
import PortfolioRiskDashboard from "./pages/portfolio/PortfolioRiskDashboard";
import ExceptionInbox from "./pages/portfolio/ExceptionInbox";
import PreVATReview from "./pages/portfolio/PreVATReview";
import PortfolioClientDetail from "./pages/portfolio/PortfolioClientDetail";

function App() {
    return (
        <AuthProvider>
            <TenantProvider>
                <BrowserRouter>
                    <Routes>
                        {/* Public Routes */}
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />
                        <Route path="/onboarding" element={<OnboardingPage />} />
                        <Route path="/pending-approval" element={<PendingApprovalPage />} />

                        {/* Protected Routes */}
                        <Route path="/app" element={<MainLayout />}>
                            {/* The first screen after login must be computed from real documents. This pointed at
                                /app/portfolio, whose data came entirely from demo fixtures. */}
                            <Route index element={<Navigate to="/app/dashboard" replace />} />
                            <Route path="dashboard" element={<MorningOverview />} />
                            <Route path="companies" element={<CompaniesPage />} />
                            {/* Primary path: the single-screen review. The old multi-step pages stay
                                reachable at /app/documents/all and /app/documents/:docId/review. */}
                            <Route path="documents" element={<ReviewScreenPage />} />
                            <Route path="documents/all" element={<DocumentsPage />} />
                            <Route path="documents/:docId/review" element={<DocumentReviewPage />} />
                            {/* Vouchers page retired (delivery consolidated onto Bank export).
                                Bookmarks land on Documents; voucher records still exist in the DB. */}
                            <Route path="vouchers" element={<Navigate to="/app/documents" replace />} />
                            {/* Legacy reconciliation hidden — redirect to Bank matching (single path). */}
                            <Route path="reconciliation" element={<Navigate to="/app/bank/matching" replace />} />
                            {/* <Route path="reconciliation" element={<ReconciliationPage />} /> */}
                            <Route path="bank" element={<BankStatementPage />} />
                            <Route path="bank/matching" element={<BankMatchingPage />} />
                            <Route path="bank/coding" element={<BankCodingPage />} />
                            <Route path="bank/export" element={<BankExportPage />} />
                            <Route path="vat" element={<VATPage />} />
                            <Route path="activity" element={<ActivityPage />} />
                            {/* Billing/plan chart — admin only; firm accountants must not see prices. */}
                            <Route path="billing" element={<RequireRole allow="admin"><BillingPage /></RequireRole>} />
                            <Route path="accountant" element={<AccountantDashboard />} />
                            <Route path="staff" element={<StaffPage />} />
                            <Route path="files" element={<FilesPage />} />
                            <Route path="admin" element={<RequireRole allow="admin"><AdminPanel /></RequireRole>} />
                            <Route path="admin/firms" element={<RequireRole allow="admin"><AdminFirmsPage /></RequireRole>} />
                            <Route path="admin/dashboard" element={<RequireRole allow="admin"><AdminDashboardPage /></RequireRole>} />
                            <Route path="admin/activity" element={<RequireRole allow="admin"><AdminActivityPage /></RequireRole>} />
                            <Route path="ai-dashboard" element={<AIDashboardPage />} />
                            <Route path="settings" element={<SettingsPage />} />
                            
                            {/* Portfolio Risk Platform Routes */}
                            <Route path="portfolio" element={<PortfolioRiskDashboard />} />
                            <Route path="portfolio/exceptions" element={<ExceptionInbox />} />
                            <Route path="portfolio/pre-vat" element={<PreVATReview />} />
                            {/* The Risk Dashboard linked here from every client row while no
                                such route existed, so the catch-all bounced users to the landing
                                page and it read as being logged out. */}
                            <Route path="portfolio/client/:clientId" element={<PortfolioClientDetail />} />
                        </Route>

                        {/* End-user inbox (business owners) — separate shell, no accountant UI */}
                        <Route path="/inbox" element={<InboxLayout />}>
                            <Route index element={<InboxPage />} />
                        </Route>

                        {/* Legacy routes - redirect to /app */}
                        <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
                        <Route path="/companies" element={<Navigate to="/app/companies" replace />} />
                        <Route path="/documents" element={<Navigate to="/app/documents" replace />} />
                        <Route path="/vouchers" element={<Navigate to="/app/documents" replace />} />
                        {/* Legacy /reconciliation alias now lands on Bank matching (single path). */}
                        <Route path="/reconciliation" element={<Navigate to="/app/bank/matching" replace />} />
                        <Route path="/vat" element={<Navigate to="/app/vat" replace />} />
                        <Route path="/activity" element={<Navigate to="/app/activity" replace />} />
                        <Route path="/billing" element={<Navigate to="/app/billing" replace />} />
                        <Route path="/accountant" element={<Navigate to="/app/accountant" replace />} />
                        <Route path="/admin" element={<Navigate to="/app/admin" replace />} />
                        <Route path="/ai-dashboard" element={<Navigate to="/app/ai-dashboard" replace />} />
                        <Route path="/settings" element={<Navigate to="/app/settings" replace />} />

                        {/* Catch all */}
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <Toaster position="top-right" richColors />
            </TenantProvider>
        </AuthProvider>
    );
}

export default App;
